import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, ShoppingCart, Menu, X, User } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { to: "/category/rims", label: "Rims" },
  { to: "/category/tires", label: "Tires" },
  { to: "/category/suspension", label: "Suspension" },
  { to: "/brands", label: "Brands" },
  { to: "/tools", label: "Tools" },
];

export default function Header() {
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [mobile, setMobile] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/search?q=${encodeURIComponent(q.trim())}`);
      setShowSearch(false);
      setMobile(false);
    }
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 border-b border-[#00f0ff]/15 bg-[#050505]/85 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="h-16 flex items-center justify-between gap-6">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
            <span className="font-display text-2xl md:text-3xl leading-none">
              <span className="bttf-text">EXTREME</span>
              <span className="text-white"> OFFSET</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => {
              const active = pathname.startsWith(n.to);
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  className="group relative text-sm font-semibold uppercase tracking-[0.15em] text-zinc-300 hover:text-white transition-colors"
                >
                  {n.label}
                  <span
                    className={`absolute -bottom-1 left-0 h-0.5 bttf-gradient transition-all duration-300 ${
                      active ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </Link>
              );
            })}
            {user?.role === "admin" && (
              <Link to="/admin" data-testid="nav-admin" className="text-sm font-semibold uppercase tracking-[0.15em] text-[#ffd400] hover:text-white transition-colors">
                Admin
              </Link>
            )}
          </nav>

          <div className="flex items-center gap-4">
            <button
              data-testid="search-toggle"
              onClick={() => setShowSearch((s) => !s)}
              className="text-zinc-300 hover:text-[#00f0ff] transition-colors"
              aria-label="Search"
            >
              <Search size={20} />
            </button>
            <Link
              to={user ? "/account" : "/login"}
              data-testid="account-link"
              className="text-zinc-300 hover:text-[#00f0ff] transition-colors"
              aria-label="Account"
            >
              <User size={20} />
            </Link>
            <button
              data-testid="cart-button"
              onClick={() => setOpen(true)}
              className="relative text-zinc-300 hover:text-[#00f0ff] transition-colors"
              aria-label="Cart"
            >
              <ShoppingCart size={20} />
              {count > 0 && (
                <span
                  data-testid="cart-count"
                  className="absolute -top-2 -right-2 bttf-gradient text-black text-[10px] font-bold h-4 min-w-4 px-1 flex items-center justify-center rounded-full"
                >
                  {count}
                </span>
              )}
            </button>
            <button
              className="lg:hidden text-zinc-300"
              onClick={() => setMobile((m) => !m)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {mobile ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {showSearch && (
          <form onSubmit={submitSearch} className="pb-4">
            <div className="flex items-center gap-3 border-b-2 border-[#00f0ff] py-2">
              <Search size={18} className="text-[#00f0ff]" />
              <input
                data-testid="search-input"
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search rims, tires, suspension, brands..."
                className="flex-1 bg-transparent outline-none text-white placeholder:text-zinc-500"
              />
            </div>
          </form>
        )}
      </div>

      {mobile && (
        <div className="lg:hidden border-t border-[#27272a] bg-[#050505] px-6 py-4 space-y-3">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              onClick={() => setMobile(false)}
              data-testid={`mobile-nav-${n.label.toLowerCase()}`}
              className="block text-lg font-semibold uppercase tracking-wide text-zinc-200"
            >
              {n.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
}
